<script setup>
/**
 * THE GUIDED COMPOSER — one task, written the way you would say it out loud.
 *
 * Replaces `TaskEditorPage.vue`, and keeps the two things on it that were right: the
 * create→`router.replace` transition, and the docked `WasloSaveBar` in the frame's footer
 * with its five states. Everything else is the redesign.
 *
 * ── THE SCREEN IS A QUERY PARAMETER, EXACTLY AS IT IS ON `BroadcastCompose` ───────────
 * `?step=job|steps|whatif|arabic|check`, plus `&at=<stepId>` for one step full screen and
 * `&set=wiring` for what it sends and gets back. FIVE SCREENS AND ZERO NEW ROUTE RECORDS,
 * which matters structurally: `SidebarGroup` resolves every child `to` through
 * `router.resolve()` inside a computed, and an unregistered name throws during render and
 * blanks the entire primary nav.
 *
 * The guard is `helpers/task-compose.js` `resolveStep`, and it corrects the URL rather than
 * the screen, so the address bar and what is on it cannot disagree. Its one property that
 * matters on the fortieth edit: A DRAFT WITH NO PROBLEMS NEVER BOUNCES — and a saved task
 * is exactly such a draft, because it passed the same gate on the way in.
 *
 * ── NOTHING REACHES THE SERVER UNTIL THERE IS SOMETHING IT WOULD ACCEPT ───────────────
 * `schema.ts:144-155` refuses a partial graph and says in as many words not to loosen it.
 * It is right: the Tasks list's on/off switch sends a whole document, so a half-written
 * definition stored behind `enabled:false` would be one flip away from a live customer. So
 * the composer never sends one. On the CREATE path nothing exists on the server until
 * "Turn this task on"; on both paths every edit is kept in `helpers/task-local-draft.js`,
 * which is the answer to `TaskEditorPage.vue:436-439` — an incomplete graph could not be
 * saved at all, and `grep localStorage` across this section returned zero.
 *
 * ── THIS PAGE OWNS EVERY REQUEST, INCLUDING "TRY IT" ──────────────────────────────────
 * A screen that fetched for itself would put one read per step on a sixteen-step task and
 * give each one its own idea of whether the connection list had loaded. So the screens are
 * handed data and emit intentions; the network lives here, once, where the loading and
 * failure states are the page's own.
 *
 * ── AND NOTHING HERE TELEPORTS ────────────────────────────────────────────────────────
 * No `Dialog`, `Popover`, `SidePanel`, `DropdownContainer` or `EmptyStateLayout` anywhere
 * in the composer. They reach `TeleportWithDirection` → `useMapGetter('accounts/isRTL')`,
 * which throws inside a render effect AFTER a test has resolved — it can never fail an
 * assertion, only the exit code, and it killed the image build on both environments. Every
 * choice here opens in place or is its own screen.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';

import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

import ArabicScreen from '../components/task/ArabicScreen.vue';
import BilingualBox from '../components/task/BilingualBox.vue';
import CheckScreen from '../components/task/CheckScreen.vue';
import DescribedNotes from '../components/task/DescribedNotes.vue';
import MissingLanguageNotice from '../components/task/MissingLanguageNotice.vue';
import ComposeRail from '../components/task/ComposeRail.vue';
import JobScreen from '../components/task/JobScreen.vue';
import RehearsalDrawer from '../components/task/RehearsalDrawer.vue';
import SpineList from '../components/task/SpineList.vue';
import StepScreen from '../components/task/StepScreen.vue';
import TaskRemove from '../components/task/TaskRemove.vue';
import WhatIfScreen from '../components/task/WhatIfScreen.vue';
import WiringScreen from '../components/task/WiringScreen.vue';
import WasloPageLayout from '../components/WasloPageLayout.vue';
import RestoreHistory from '../components/RestoreHistory.vue';
import WasloSaveBar from '../components/WasloSaveBar.vue';
import { useWasloI18n } from '../i18n.js';
import { useWasloApi } from '../composables/useWasloApi.js';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';
import { useCustomerLanguages } from '../composables/useCustomerLanguages.js';
import { toIntlLocale } from '../helpers/intl.js';
import {
  blankDraft,
  composeProblems,
  definitionOf,
  draftMatchesSaved,
  draftFromTask,
  isStepComplete,
  problemsForStep,
  resolveStep,
  startOf,
  stepAfter,
  stepBefore,
  stepsFor,
} from '../helpers/task-compose.js';
import { channelChoices } from '../helpers/task-channels.js';
import { menusStartingTask } from '../helpers/menus-starting.js';
import { accountChannels } from '../api/channels.js';
import { removedFromForm } from '../helpers/form-layout.js';
import {
  definitionToSpine,
  skipWhenWorks,
  spineJumps,
} from '../helpers/task-spine.js';
import {
  addRowAt,
  moveProblem,
  moveRowAt,
  removeRowAt,
  replaceRowAt,
  sameGraph,
} from '../helpers/task-spine-edit.js';
import { MAX_CTA, withCardWords } from '../helpers/task-arabic.js';
import { fieldFor, taskMissingWords } from '../helpers/missing-language.js';
import { purposeOf } from '../helpers/connection-draft.js';
import {
  arrivingKeys,
  arrivingKeysOnSomePath,
  confirmedChangeNotes,
  graphWarnings,
  liveSample,
  normaliseProblems,
  problemMessage,
  profileFieldFor,
  problemsByStep,
  writtenKeys,
} from '../helpers/task-flow-draft.js';
import { starterById, starterDefinition } from '../helpers/task-starters.js';
import { draftToWire, toDraft as toMenuDraft } from '../helpers/menu-draft.js';
import { sendsByKey } from '../helpers/task-outward.js';
import { namedAnswers } from '../helpers/task-wiring.js';
import {
  NEW_TASK,
  age as draftAge,
  clear as clearLocalDraft,
  read as readLocalDraft,
  write as writeLocalDraft,
} from '../helpers/task-local-draft.js';
import { WASLO_ERROR_CODES, wasloApi, withConfirm } from '../api/client.js';

const { t, locale } = useWasloI18n();
const route = useRoute();
const router = useRouter();
const accountId = useWasloAccountId();
/** P-7.1: "Your customers write in". Both until read, and Both when it cannot be. */
const languages = useCustomerLanguages(accountId);
/** What the screen sequence and its gates are told about the account. */
const composeCtx = computed(() => ({ customerLanguages: languages.value }));

const key = name => `WASLO.TASKS.COMPOSE.${name}`;

const positiveInt = value => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

/**
 * Local rather than a straight computed off the route: after a successful create this is
 * set from the response BEFORE the navigation, so the page becomes an edit in one tick
 * rather than briefly rendering as a screen that saves by POST.
 */
const taskId = ref(positiveInt(route?.params?.taskId));
const isNew = computed(() => taskId.value === null);

const draft = ref(blankDraft());
/** What the server last told us, in wire shape — the only thing the dirty check reads. */
const savedWire = ref(null);
const unreadable = ref(false);
const unreadableReasons = ref([]);
const ready = ref(false);
const isSaving = ref(false);
const saveFailed = ref(false);
/**
 * T-16: what the server refused the last save for (`skip_feeds_connection` among them), on the
 * cards it names, until the next edit. A bare `catch {}` used to hide it behind "Save failed".
 */
const serverProblems = ref([]);
const savedAt = ref(null);
/** A kept draft found on open, until it is taken or discarded. */
const kept = ref(null);
/** Set by the page's own navigations, which are never an abandonment. */
const bypassGuard = ref(false);
/** `{ [stepId]: { running, result, errorMessage, fieldErrors } }` — one "Try it" each. */
const tests = ref({});
/** `{ [connectionName]: params[] | null }`, filled in as the list names one. */
const connectionParams = ref({});
/**
 * `{ [connectionName]: string[] }`: the details Waslo fills in that each one sends (P-3.1),
 * drawn as locked rows on the wiring screen.
 */
const connectionContactDetails = ref({});

const task = useWasloApi(
  signal => wasloApi.getTask(accountId.value, taskId.value, signal),
  { immediate: false }
);

const connections = useWasloApi(
  signal => wasloApi.getTaskConnections(accountId.value, signal),
  { isEmpty: () => false }
);

const fullConnections = useWasloApi(
  signal => wasloApi.getConnections(accountId.value, signal),
  { immediate: false, isEmpty: () => false }
);

/**
 * The three contact columns a step may write an answer to, spelled as Chatwoot spells them
 * (`STANDARD_CONTACT_FIELDS` in the sidecar's `actions/contact-fields.ts`). A literal, not
 * a read: they are columns on a table, and a third request to be told the same three names
 * would be a third way for this page to fail to open.
 */
const CONTACT_FIELDS = ['name', 'email', 'phone_number'];

// ── what the draft compiles to ─────────────────────────────────────────────────────
// The spine's steps AND the form's button (`definitionOf`, P-7.1).
const definition = computed(() => definitionOf(draft.value));
const rows = computed(() => draft.value.spine?.rows ?? []);
// ── what each connection does (P-3.4) ──────────────────────────────────────────────
// Read off the two lists already loaded above; declared here, BEFORE `problems`, so no
// computed reaches a binding that is not initialised yet.
const connectionPurposes = computed(() => {
  const out = {};
  (fullConnections.data.value ?? connections.data.value ?? []).forEach(row => {
    const purpose = purposeOf(row);
    if (purpose) out[row.name] = purpose;
  });
  return out;
});
const changeConnections = computed(
  () =>
    new Set(
      Object.keys(connectionPurposes.value).filter(
        name => connectionPurposes.value[name] === 'change'
      )
    )
);
/** `list`, plus the server's refusals it does not already say, once each. */
const withServerProblems = list => {
  const said = new Set(
    list.map(p => `${p.stepId}|${p.problem}|${p.detail ?? ''}`)
  );
  return [
    ...list,
    ...serverProblems.value.filter(
      p => !said.has(`${p.stepId}|${p.problem}|${p.detail ?? ''}`)
    ),
  ];
};
// An edit to the graph is an answer to what the server said; the next save asks it again.
watch(definition, () => {
  if (serverProblems.value.length) serverProblems.value = [];
});
const problems = computed(() =>
  withServerProblems(
    composeProblems(draft.value, {
      changeConnections: changeConnections.value,
      customerLanguages: languages.value,
    })
  )
);
/**
 * Whether each connection is on, once the list has loaded (P-10.21's `connection_off`).
 * `undefined` while loading, so the Check screen never warns about a list it has not read.
 */
const connectionEnabled = computed(() => {
  const list = fullConnections.data.value ?? connections.data.value;
  if (!Array.isArray(list)) return undefined;
  return Object.fromEntries(list.map(row => [row.name, row.enabled === true]));
});
const warnings = computed(() =>
  graphWarnings(definition.value, {
    purposes: connectionPurposes.value,
    params: connectionParams.value,
    // P-3.7: which of the details Waslo fills in each connection sends.
    contactDetails: connectionContactDetails.value,
    enabled: connectionEnabled.value,
  })
);
const changeNotes = computed(() =>
  confirmedChangeNotes(
    definition.value,
    changeConnections.value,
    String(locale.value ?? '').startsWith('ar') ? 'ar' : 'en'
  )
);
const shownSteps = computed(() => stepsFor(draft.value, composeCtx.value));
const jumps = computed(() => spineJumps(draft.value.spine));

const savedDefinition = computed(() => savedWire.value?.definition ?? null);

/**
 * `sameGraph` and NOT `JSON.stringify`, and the difference is a live bug.
 *
 * Compiling a stored step writes its links and its options back on after its body, so the
 * key ORDER differs from what came off the wire while every value is identical. A
 * stringify compare therefore calls a freshly-loaded task dirty, every rename carries a
 * `definition`, and the server bumps `version` only on a real change — so a title edit
 * would read as a graph edit and drop every chat running the task. See
 * `helpers/task-spine-edit.js`.
 */
const same = (a, b) => sameGraph(a, b);

const patch = computed(() => {
  const out = {};
  if (!savedWire.value) return out;
  if (draft.value.title.trim() !== savedWire.value.title) {
    out.title = draft.value.title.trim();
  }
  if (draft.value.trigger.trim() !== savedWire.value.trigger) {
    out.trigger = draft.value.trigger.trim();
  }
  if (draft.value.enabled !== savedWire.value.enabled) {
    out.enabled = draft.value.enabled;
  }
  // P-10.26: sent only when it changed, like every other column. Compared as sets of words in
  // the one order the composer writes them.
  if (
    JSON.stringify(draft.value.channels ?? null) !==
    JSON.stringify(savedWire.value.channels ?? null)
  ) {
    out.channels = draft.value.channels ?? null;
  }
  // ABSENT means "leave the stored graph alone", which is what makes the list screen's
  // on/off switch safe. So a rename must never carry one.
  if (!same(definition.value, savedDefinition.value)) {
    out.definition = definition.value;
  }
  return out;
});

const isDirty = computed(() => Object.keys(patch.value).length > 0);

// ── the screen, and the guard in front of it ───────────────────────────────────────
const requestedStep = computed(() => String(route.query.step ?? 'job'));
const step = computed(() =>
  ready.value
    ? resolveStep(requestedStep.value, draft.value, composeCtx.value)
    : requestedStep.value
);

const basePath = computed(() =>
  accountId.value === null
    ? ''
    : frontendURL(
        isNew.value
          ? `accounts/${accountId.value}/waslo/tasks/new`
          : `accounts/${accountId.value}/waslo/tasks/${taskId.value}`
      )
);

const linkFor = (target, extra = {}) => ({
  path: basePath.value,
  query: { step: target, ...extra },
});

const stepsLink = computed(() => linkFor('steps'));
// Where the Arabic screen's "missing" count points: the warning on the Check screen.
const checkLink = computed(() => linkFor('check'));
const stepLinkFor = stepId => linkFor('steps', { at: stepId });

// ── one step, full screen ──────────────────────────────────────────────────────────
const openStepId = computed(() => String(route.query.at ?? ''));
const openIndex = computed(() =>
  rows.value.findIndex(row => row.step?.id === openStepId.value)
);
const openRow = computed(() =>
  openIndex.value === -1 ? null : rows.value[openIndex.value]
);
const showsWiring = computed(
  () => openRow.value !== null && String(route.query.set ?? '') === 'wiring'
);

const wiringLink = computed(() =>
  linkFor('steps', { at: openStepId.value, set: 'wiring' })
);

// Whenever the URL asks for more than the draft has earned, correct the URL — never the
// other way round.
watch(
  [step, requestedStep, ready],
  ([resolved, requested, isReady]) => {
    if (!isReady || accountId.value === null) return;
    if (resolved !== requested) router.replace(linkFor(resolved));
  },
  { immediate: true }
);

/**
 * The screens the rail may link to: every completed one, plus the first that is not.
 *
 * Sliced rather than looped, because it is exactly a prefix — the guard admits a screen
 * only when everything before it is complete. Deriving it any other way is a second rule
 * that can disagree with the guard.
 */
const reachable = computed(() => {
  const first = shownSteps.value.findIndex(
    candidate => !isStepComplete(candidate, draft.value, composeCtx.value)
  );
  return first === -1
    ? [...shownSteps.value]
    : shownSteps.value.slice(0, first + 1);
});

const nextStep = computed(() =>
  stepAfter(step.value, draft.value, composeCtx.value)
);
const previousStep = computed(() =>
  stepBefore(step.value, draft.value, composeCtx.value)
);
const canAdvance = computed(() =>
  isStepComplete(step.value, draft.value, composeCtx.value)
);

/**
 * What stands where the Next button would be — the FIRST thing missing, by name.
 *
 * "Finish what is marked above" is true and useless on the first screen of a new task,
 * where nothing is marked yet because nothing has been touched. The two task-level kinds
 * have their own sentences here; everything else is a step's, and `problemMessage` already
 * owns every one of those (including an unknown kind from a newer sidecar).
 */
const NEEDS_KEY = {
  no_title: 'JOB.TITLE_REQUIRED',
  title_long: 'JOB.TITLE_LONG',
  no_trigger: 'JOB.TRIGGER_REQUIRED',
  trigger_long: 'JOB.TRIGGER_LONG',
};

const needsLine = computed(() => {
  const first = problemsForStep(step.value, draft.value, composeCtx.value)[0];
  if (!first) return t(key('NEEDS'));
  const own = NEEDS_KEY[first.problem];
  return own ? t(key(own)) : problemMessage(first, t);
});

const position = computed(() =>
  t(key('POSITION'), {
    number: shownSteps.value.indexOf(step.value) + 1,
    total: shownSteps.value.length,
  })
);

const byStep = computed(() => problemsByStep(problems.value));

const stepProblems = computed(() =>
  problemsByStep(
    withServerProblems(problemsForStep('steps', draft.value, composeCtx.value))
  )
);

const takenKeysFor = index =>
  writtenKeys(definition.value)
    .filter(row => row.stepId !== rows.value[index]?.step?.id)
    .map(row => row.key);

/**
 * F3.7: an earlier answer as the owner reads it, "The studio they picked (Pick a studio)":
 * what it is, how it was got, and her own name for the step that got it.
 */
const answerLabel = row =>
  t(`WASLO.TASKS.COMPOSE.WIRING.ANSWER_${row.kind}`, {
    answer: row.answer,
    step: row.step || t('WASLO.TASKS.COMPOSE.SCREEN.UNWRITTEN'),
  });

/** Chat keys saved before this step on EVERY path — the set `validate.ts` enforces. */
const availableKeys = computed(() => {
  const arriving = arrivingKeys(definition.value).get(openStepId.value);
  // P-2.3: what a choice's answer carries to a system, for the wiring screen's "sends:" line.
  const sends = sendsByKey(definition.value?.steps);
  return namedAnswers(definition.value, [...(arriving ?? [])]).map(row => ({
    value: row.value,
    label: answerLabel(row),
    ...(sends[row.value] ? { sends: sends[row.value] } : {}),
  }));
});

/** F3.7: keys saved on only SOME ways to this step. Named, never offered. */
const someOnlyKeys = computed(() => {
  const every =
    arrivingKeys(definition.value).get(openStepId.value) ?? new Set();
  const some =
    arrivingKeysOnSomePath(definition.value).get(openStepId.value) ?? new Set();
  return namedAnswers(
    definition.value,
    [...some].filter(name => !every.has(name))
  ).map(row => ({ value: row.value, label: answerLabel(row) }));
});

// ── editing the list ───────────────────────────────────────────────────────────────
const setSpine = spine => {
  draft.value = { ...draft.value, spine };
  if (saveFailed.value) saveFailed.value = false;
};

const onAdd = ({ index, kind }) => {
  const made = addRowAt(draft.value.spine, index, kind);
  setSpine(made.spine);
  if (accountId.value !== null) router.push(stepLinkFor(made.id));
};

const onMove = ({ index, delta }) =>
  setSpine(moveRowAt(draft.value.spine, index, delta));

const onReplace = (index, row) =>
  setSpine(replaceRowAt(draft.value.spine, index, row));

/**
 * The Check screen's one-click fixes (P-2.2, P-10.23, P-10.21). Each does exactly what its
 * button says, on the draft, and nothing is saved until the owner saves.
 */
const onFix = warning => {
  const list = rows.value;
  const at = list.findIndex(row => row.step?.id === warning.stepId);
  if (warning.problem === 'failure_path_on_normal_path') {
    const failAt = list.findIndex(row => row.step?.id === warning.detail);
    if (at < 0 || failAt < 0) return;
    setSpine({ ...draft.value.spine, rows: skipWhenWorks(list, at, failAt) });
  } else if (warning.problem === 'kept_for_chat_only' && at >= 0) {
    const row = list[at];
    const saveTo = { ...row.step.saveTo, contact: profileFieldFor(row.step) };
    onReplace(at, { ...row, step: { ...row.step, saveTo } });
  } else if (warning.problem === 'connection_off' && accountId.value !== null) {
    router.push({
      name: 'waslo_settings_connections',
      params: { accountId: accountId.value },
    });
  }
};

/**
 * ONE ROW OF THE WORDS-ON-THE-CARD SWEEP, WRITTEN BACK INTO THE SPINE.
 *
 * `ArabicScreen` addresses a row by `stepId`/`optionId` rather than by list index, because
 * it is fed a DEFINITION shape (`{steps}` with flat `options`) and knows nothing about the
 * spine's wrapped rows. `withCardWords` is the one place that knows which wire field a row
 * means, so the translation between the two shapes happens here and only here: flatten the
 * row's options into the step body, let the helper write, then put the outcomes back where
 * the spine keeps them. `bodyOf` strips `options` off a stored step, so building the flat
 * body cannot collide with a key the spine row already carries.
 */
const onCardWords = ({ stepId, optionId, kind, lang, text }) => {
  const index = rows.value.findIndex(row => row.step?.id === stepId);
  if (index === -1) return;
  const current = rows.value[index];
  const wrapped = Array.isArray(current.options) ? current.options : [];
  const written = withCardWords(
    { ...current.step, options: wrapped.map(entry => entry?.option ?? {}) },
    { stepId, optionId, kind, lang },
    text
  );
  // `body`, not `step`: the page already has a `step` computed (which screen is open) and
  // shadowing it here would read as the wrong thing at a glance.
  const { options: writtenOptions, ...body } = written;
  onReplace(index, {
    ...current,
    step: body,
    ...(wrapped.length > 0
      ? {
          options: wrapped.map((entry, at) => ({
            ...entry,
            option: writtenOptions[at],
          })),
        }
      : {}),
  });
};

// ── P-7.1: every field written in one language only, and the words that fill it ─────
const missingWords = computed(() =>
  taskMissingWords(definition.value, draft.value.cta, languages.value)
);

/** Can this task ever be sent as a form? Only a question can be a form field. */
const formCapable = computed(() =>
  rows.value.some(
    row => row.step?.type === 'ask' || row.step?.type === 'choose'
  )
);

const setCta = changes => {
  draft.value = {
    ...draft.value,
    cta: {
      en: draft.value.cta?.en ?? '',
      ar: draft.value.cta?.ar ?? '',
      ...changes,
    },
  };
};

/** One "Use this" from the warning, written to the field the row names (`fieldFor`). */
const applyWords = ({ row, text }) => {
  const { target } = row;
  if (target.stepId === '') {
    setCta({ [row.to]: text });
    return;
  }
  const index = rows.value.findIndex(entry => entry.step?.id === target.stepId);
  if (index === -1) return;
  const current = rows.value[index];
  const field = fieldFor(target, row.to);
  if (target.optionId === null) {
    onReplace(index, { ...current, step: { ...current.step, [field]: text } });
    return;
  }
  onReplace(index, {
    ...current,
    options: (current.options ?? []).map(entry =>
      entry?.option?.id === target.optionId
        ? { ...entry, option: { ...entry.option, [field]: text } }
        : entry
    ),
  });
};

/**
 * The words-on-the-card sweep's translator: ONE batch call for the rows it was handed (at
 * most 20, each with words to translate from), with the price the owner confirmed.
 */
const translateCardWords = async (sweepRows, credits) => {
  const result = await wasloApi.translateBatch(
    accountId.value,
    {
      from: 'en',
      to: 'ar',
      items: sweepRows.map(row => ({
        kind: row.kind === 'option' ? 'task_option_label' : 'task_question',
        text: row.other,
      })),
    },
    undefined,
    withConfirm(credits)
  );
  return {
    charged: result.charged,
    proposals: result.items.flatMap((item, at) =>
      item.outcome === 'ok' && sweepRows[at]
        ? [
            {
              stepId: sweepRows[at].stepId,
              optionId: sweepRows[at].optionId,
              kind: sweepRows[at].kind,
              text: item.text,
            },
          ]
        : []
    ),
  };
};

const onRemove = index => {
  setSpine(removeRowAt(draft.value.spine, index));
  if (accountId.value !== null) router.replace(stepsLink.value);
};

const moveProblemFor = (index, delta) =>
  moveProblem(draft.value.spine, index, delta);

// ── P-8.2: a task drafted from her description ─────────────────────────────────────
/**
 * What came with the draft: the hand-overs to a system she has not connected, what Waslo could not
 * finish, the menu option and the approved message. `null` on every task not drafted here. Kept on
 * the page only: a reload keeps the draft itself (the local draft), not these notes.
 */
const described = ref(null);
/** "Also add it to my menu": pre-ticked, acted on only when the task is turned on. */
const menuChoice = ref({ add: true, menuId: null });
const menus = ref(null);

const useDescribed = result => {
  const read = draftFromTask({
    title: result.title,
    trigger: result.trigger,
    enabled: false,
    definition: result.definition,
    channels: null,
  });
  draft.value = { ...read.draft, enabled: false };
  unreadable.value = read.unreadable;
  unreadableReasons.value = read.reasons ?? [];
  described.value = {
    notes: result.notes,
    problems: result.problems,
    menuOption: result.menuOption,
    approvedMessage: result.approvedMessage,
  };
  menuChoice.value = { add: result.menuOption !== null, menuId: null };
  if (result.menuOption !== null && accountId.value !== null) {
    wasloApi
      .getMenus(accountId.value)
      .then(list => {
        menus.value = list;
        const first = list.find(menu => menu.optionCount < 10);
        menuChoice.value = { ...menuChoice.value, menuId: first?.id ?? null };
      })
      .catch(() => {
        menus.value = [];
      });
  }
  if (accountId.value !== null) router.replace(checkLink.value);
};

/** After "Turn this task on": the menu option she kept ticked, added to the menu she chose. */
const addDescribedOption = async () => {
  const option = described.value?.menuOption;
  const { add, menuId } = menuChoice.value;
  if (!option || !add || menuId === null || taskId.value === null) return;
  try {
    const menu = await wasloApi.getMenu(accountId.value, menuId);
    const wire = draftToWire(toMenuDraft(menu));
    await wasloApi.updateMenu(accountId.value, menuId, {
      ...wire,
      options: [
        ...wire.options,
        {
          titleEn: option.titleEn,
          titleAr: option.titleAr,
          taskId: taskId.value,
        },
      ],
    });
    described.value = { ...described.value, menuOption: null };
  } catch (error) {
    useAlert(error?.message || t('WASLO.SETUP_AI.DESCRIBE.MENU_FAILED'));
  }
};

const useStarter = id => {
  const starter = starterById(id);
  const made = definitionToSpine(starterDefinition(id));
  if (!starter || !made.spine) return;
  const arabic = String(locale.value ?? '').startsWith('ar');
  draft.value = {
    ...draft.value,
    title:
      draft.value.title.trim() ||
      (arabic ? starter.taskTitleAr : starter.taskTitleEn),
    trigger:
      draft.value.trigger.trim() ||
      (arabic ? starter.triggerAr : starter.triggerEn),
    spine: made.spine,
  };
  unreadable.value = false;
  unreadableReasons.value = [];
};

// ── reading the task in ────────────────────────────────────────────────────────────
const wireOf = value => ({
  title: typeof value?.title === 'string' ? value.title.trim() : '',
  trigger: typeof value?.trigger === 'string' ? value.trigger.trim() : '',
  enabled: value?.enabled !== false,
  definition: value?.definition ?? null,
  // P-10.26: a column of the row, not part of the graph; `null` is every channel.
  channels: Array.isArray(value?.channels) ? [...value.channels] : null,
});

const adopt = value => {
  const read = draftFromTask(value);
  draft.value = read.draft;
  unreadable.value = read.unreadable;
  unreadableReasons.value = read.reasons ?? [];
  const wire = wireOf(value);
  // P-5.8: THE BASELINE IS THE ROUND-TRIP, computed once here. The draft is the builder's
  // reading of the stored graph, and comparing it with the RAW stored graph called every
  // task saved by an older build "Not saved yet" the moment it was opened. An unreadable
  // graph keeps its raw baseline: the builder could not read it, so nothing it produces is
  // "the same" task, and a free-text task (`null`) keeps its `null`.
  savedWire.value =
    read.unreadable || wire.definition === null
      ? wire
      : { ...wire, definition: definitionOf(read.draft) };
};

/**
 * A draft kept in this browser, offered rather than applied.
 *
 * Applied silently it would be indistinguishable from the server having the wrong task in
 * it — the one thing a page that is already about trust must never do. So it is a sentence
 * with two buttons, and `age` is in it because "from 12 minutes ago" is half the decision.
 */
const offerKeptDraft = () => {
  const id = taskId.value ?? NEW_TASK;
  const found = readLocalDraft(accountId.value, id);
  if (!found) return;
  // P-5.8: a kept draft that IS the saved task is not work, and is dropped for good here
  // (the one-time cleanup of the phantom drafts older builds wrote on every open).
  if (savedWire.value && draftMatchesSaved(found.draft, savedWire.value)) {
    clearLocalDraft(accountId.value, id);
    return;
  }
  if (savedWire.value && same(found.draft, draft.value)) return;
  kept.value = found;
};

const loadTask = async () => {
  ready.value = false;
  await task.reload();
  if (task.data.value) adopt(task.data.value);
  offerKeptDraft();
  ready.value = true;
};

const takeKeptDraft = () => {
  const keptDraft = kept.value?.draft;
  if (keptDraft) {
    // P-4.10: work kept by a build before forms had pages has no `form` key; taking it must not
    // wipe a layout arranged since on Arrange form.
    draft.value =
      'form' in keptDraft
        ? keptDraft
        : { ...keptDraft, form: draft.value.form ?? null };
  }
  kept.value = null;
};

const discardKeptDraft = () => {
  clearLocalDraft(accountId.value, taskId.value ?? NEW_TASK);
  kept.value = null;
};

/**
 * A save of this task was restored from its history (P-6.11). The server's task is now the one
 * to show, so a draft kept in this browser for it is dropped rather than offered over it.
 */
const historyRef = ref(null);
const onTaskRestored = () => {
  clearLocalDraft(accountId.value, taskId.value ?? NEW_TASK);
  kept.value = null;
  loadTask();
};

onMounted(() => {
  if (isNew.value) {
    offerKeptDraft();
    ready.value = true;
    return;
  }
  loadTask();
});

const isFetching = computed(() => !ready.value || connections.isFetching.value);

// Only the task read can fail the page. A 404 is its own sentence.
const errorMessage = computed(() => {
  const error = task.error.value;
  if (!error) return '';
  return error.code === WASLO_ERROR_CODES.NOT_FOUND
    ? t('WASLO.TASKS.EDITOR.NOT_FOUND')
    : t('WASLO.COMMON.ERROR_BODY');
});

// ── keeping the work ───────────────────────────────────────────────────────────────
// Every edit, kept where a route change cannot unmount it. `write` reports whether it
// stuck rather than throwing: quota is a normal outcome in a private window, and losing
// the edit to the mechanism whose whole job is not losing it would be the worst bug here.
// P-5.8: KEPT ONLY WHILE IT IS REAL WORK. A new task, or a saved one with an edit; an edit
// undone back to the saved task clears what was kept, so "we kept your draft" is only ever
// offered for something she actually changed.
watch(
  draft,
  value => {
    if (!ready.value || accountId.value === null) return;
    const id = taskId.value ?? NEW_TASK;
    if (isNew.value || isDirty.value)
      writeLocalDraft(accountId.value, id, value);
    else clearLocalDraft(accountId.value, id);
  },
  { deep: true }
);

const keptAgeMinutes = computed(() => {
  const ms = draftAge(kept.value);
  return ms === null ? 0 : Math.max(1, Math.round(ms / 60000));
});

// ── the save bar ───────────────────────────────────────────────────────────────────
const savedTime = computed(() =>
  savedAt.value
    ? new Intl.DateTimeFormat(toIntlLocale(locale.value), {
        hour: 'numeric',
        minute: '2-digit',
      }).format(savedAt.value)
    : ''
);

const saveState = computed(() => {
  if (isSaving.value) return 'saving';
  if (saveFailed.value) return 'error';
  if (isNew.value || isDirty.value) return 'dirty';
  if (savedAt.value) return 'saved';
  return 'idle';
});

const saveStatus = computed(() => {
  if (isSaving.value) return t('WASLO.TASKS.EDITOR.STATUS.SAVING');
  if (saveFailed.value) return t('WASLO.TASKS.EDITOR.STATUS.ERROR');
  if (isNew.value) return t('WASLO.TASKS.EDITOR.STATUS.NEW');
  if (isDirty.value) return t('WASLO.TASKS.EDITOR.STATUS.DIRTY');
  if (savedAt.value) {
    return t('WASLO.TASKS.EDITOR.STATUS.SAVED', { time: savedTime.value });
  }
  return t('WASLO.TASKS.EDITOR.STATUS.IDLE');
});

/**
 * IS THIS TASK ANSWERING CUSTOMERS RIGHT NOW? Read off the SERVER's row and nothing else.
 *
 * `savedWire` is what came back from the last read or write. `draft.enabled` is an
 * intention and is not a customer reading anything, and a task that has never been created
 * is not live however the draft is set. Three states, and the difference between the first
 * two is worth a different sentence: "nothing exists yet" and "it exists and is switched
 * off" are not the same promise.
 */
const liveState = computed(() => {
  if (savedWire.value === null) return 'none';
  return savedWire.value.enabled ? 'on' : 'off';
});

const isLive = computed(() => liveState.value === 'on');

/** The line under the rail, on every screen. */
const liveLine = computed(() =>
  t(key(`LIVE.${liveState.value.toUpperCase()}`))
);

/** The pill in the save bar, which is on screen at every scroll position. */
const liveNote = computed(() =>
  t(key(`LIVE.CHIP_${liveState.value.toUpperCase()}`))
);

/**
 * WHAT THE SAVE BAR MAY SAY, and the one thing it may no longer say.
 *
 * The idle arm used to be `EDITOR.STATUS.IDLE_DETAIL` — "Your assistant is using this
 * now." — which is a claim about LIVENESS made from the SAVE state. It is false on any
 * saved task that is switched off, and the reverse case is worse: on a live task with an
 * unfinished edit this line said "Some steps still need finishing" and the fact that
 * customers were being served by the stored version appeared nowhere at all.
 *
 * So liveness left this line entirely and became the bar's `note`, which is rendered in
 * every state. What is left here is only ever about saving. `SAVE_READY` was reworded for
 * the same reason: "Nothing has been sent to your customers yet" is a sentence about a new
 * task that was being shown while editing a task in its fourth month of service.
 */
const saveDetail = computed(() => {
  if (isSaving.value) return '';
  if (saveFailed.value) return t('WASLO.TASKS.EDITOR.STATUS.ERROR_DETAIL');
  if (problems.value.length > 0) return t(key('SAVE_BLOCKED'));
  if (isNew.value || isDirty.value) return t(key('SAVE_READY'));
  return '';
});

const saveLabel = computed(() =>
  isNew.value ? t('WASLO.TASKS.EDITOR.CREATE') : t('WASLO.TASKS.EDITOR.SAVE')
);

/**
 * The one write. A graph with problems is a graph the SERVER will refuse, so it is not
 * sent: the screens already name every one of them, on the step each belongs to.
 *
 * `setEnabled` is THREE-VALUED on purpose. `null` — the docked save bar — means "write
 * what changed and do not touch the switch", which is what keeps an ordinary save on a
 * live task from silently turning it off now that a draft starts off. `true` and `false`
 * are the Check screen's two named buttons, and each does only what its label says. The
 * predecessor took a single `turnOn` boolean that could only ever switch a task ON, so the
 * button labelled "Save it, switched off" turned it on.
 */
const save = async (setEnabled = null) => {
  if (isSaving.value) return;
  if (problems.value.length > 0) {
    useAlert(t(key('SAVE_BLOCKED')));
    return;
  }
  if (setEnabled !== null && draft.value.enabled !== setEnabled) {
    draft.value = { ...draft.value, enabled: setEnabled };
  }
  if (!isNew.value && !isDirty.value) return;

  isSaving.value = true;
  saveFailed.value = false;
  try {
    if (isNew.value) {
      const created = await wasloApi.createTask(accountId.value, {
        title: draft.value.title.trim(),
        trigger: draft.value.trigger.trim(),
        enabled: draft.value.enabled,
        definition: definition.value,
        ...(draft.value.channels ? { channels: draft.value.channels } : {}),
      });
      clearLocalDraft(accountId.value, NEW_TASK);
      adopt(created);
      taskId.value = created.id;
      savedAt.value = new Date();
      useAlert(t('WASLO.TASKS.EDITOR.CREATED'));
      if (accountId.value !== null) {
        bypassGuard.value = true;
        // Replace, not push: "new task" is not somewhere to come back to.
        await router.replace({
          path: frontendURL(
            `accounts/${accountId.value}/waslo/tasks/${created.id}`
          ),
          query: { step: 'check' },
        });
      }
    } else {
      // Render from what came back, not from what we sent: the BFF trims, normalises and
      // echoes the SAVED state, and disagreeing with it is how a page starts lying.
      const saved = await wasloApi.updateTask(
        accountId.value,
        taskId.value,
        patch.value
      );
      adopt(saved);
      clearLocalDraft(accountId.value, taskId.value);
      historyRef.value?.reload();
      savedAt.value = new Date();
      useAlert(t('WASLO.TASKS.EDITOR.SAVED'));
    }
  } catch (err) {
    // T-16: a refused graph names its problems; they go on the cards, not into a toast.
    serverProblems.value = normaliseProblems(err?.problems);
    saveFailed.value = true;
    useAlert(t('WASLO.TASKS.EDITOR.SAVE_FAILED'));
  } finally {
    isSaving.value = false;
  }
};

// ── how it starts and where it runs (P-10.9, P-10.26) ───────────────────────────────
/**
 * P-10.26: the account's channels, read from Chatwoot. A failed read is `[]`, and the composer
 * then offers all seven rather than none.
 */
const accountChannelList = useWasloApi(
  signal => accountChannels(accountId.value, signal),
  { isEmpty: () => false }
);
const channelOptions = computed(() =>
  channelChoices(accountChannelList.data.value ?? [], draft.value.channels)
);

// ── the form (P-4.10) ────────────────────────────────────────────────────────────────
/**
 * The Check screen's "Form" line links to Arrange form for a SAVED task that can carry a form,
 * on an account with a WhatsApp number. Whether that number may carry a form is decided where it
 * is published (the Forms tab asks the eligibility verdict) and again before every send; reading
 * it here would cost a Graph call on every visit to the Check screen.
 */
const hasWhatsapp = computed(() =>
  (accountChannelList.data.value ?? []).includes('whatsapp')
);
const formLink = computed(() =>
  !isNew.value &&
  formCapable.value &&
  hasWhatsapp.value &&
  accountId.value !== null &&
  taskId.value !== null
    ? {
        name: 'waslo_task_form',
        params: {
          accountId: String(accountId.value),
          taskId: String(taskId.value),
        },
      }
    : null
);
/** P-4.10 design 4: the questions this draft deleted that the saved form carried. */
const formRemoved = computed(() =>
  removedFromForm(
    savedDefinition.value,
    definition.value,
    String(locale.value ?? '').startsWith('ar') ? 'ar' : 'en'
  )
);

/**
 * P-10.9: the menus that start this task, for a menu-started one. `null` while unknown (not a
 * menu-started task, or the read failed), which says nothing; `[]` is a real "none".
 */
const menusStarting = ref(null);
let menusAsk = 0;
const loadMenusStarting = async () => {
  menusAsk += 1;
  const asked = menusAsk;
  if (startOf(definition.value).mode !== 'menu' || accountId.value === null) {
    menusStarting.value = null;
    return;
  }
  if (taskId.value === null) {
    menusStarting.value = [];
    return;
  }
  try {
    const list = await wasloApi.getMenus(accountId.value);
    const details = await Promise.all(
      list
        .filter(menu => menu.enabled)
        .map(menu => wasloApi.getMenu(accountId.value, menu.id))
    );
    if (asked === menusAsk) {
      menusStarting.value = menusStartingTask(details, taskId.value);
    }
  } catch {
    if (asked === menusAsk) menusStarting.value = null;
  }
};
watch(
  () => [startOf(definition.value).mode, taskId.value, accountId.value],
  loadMenusStarting,
  { immediate: true }
);
const noMenu = computed(
  () =>
    startOf(definition.value).mode === 'menu' &&
    Array.isArray(menusStarting.value) &&
    menusStarting.value.length === 0
);

// ── whether this version was tried (P-2.11) ──────────────────────────────────────────
/**
 * Re-read on the Check screen whenever the graph changes (a new graph is a new record) and after
 * every answered rehearsal message. A failed read is `null`, which draws no line.
 */
const rehearsal = ref(null);
let rehearsalAsk = 0;
const loadRehearsal = async () => {
  rehearsalAsk += 1;
  const asked = rehearsalAsk;
  if (accountId.value === null || !definition.value) {
    rehearsal.value = null;
    return;
  }
  try {
    const status = await wasloApi.getRehearsalStatus(
      accountId.value,
      definition.value
    );
    if (asked === rehearsalAsk) rehearsal.value = status;
  } catch {
    if (asked === rehearsalAsk) rehearsal.value = null;
  }
};
watch(
  () => [step.value, JSON.stringify(definition.value)],
  () => {
    if (step.value === 'check') loadRehearsal();
  },
  { immediate: true }
);

/** P-2.11: each connection's sample fields, by name, for the what-if screen's picker. */
const samplePathsByConnection = computed(() => {
  const out = {};
  (connections.data.value ?? []).forEach(row => {
    out[row.name] = row.samplePaths ?? [];
  });
  return out;
});

const rehearsalDrawer = ref(null);
const openRehearsal = () => rehearsalDrawer.value?.open();

// ── "Try it": one real call to the owner's own system ──────────────────────────────
const choicesHaveIds = computed(() =>
  (connections.data.value ?? []).some(row => typeof row.id === 'number')
);

const connectionIdByName = computed(() => {
  const out = {};
  [fullConnections.data.value ?? [], connections.data.value ?? []].forEach(
    list => {
      list.forEach(row => {
        if (typeof row.id === 'number') out[row.name] = row.id;
      });
    }
  );
  return out;
});

const graphConnections = computed(
  () => fullConnections.data.value ?? connections.data.value ?? null
);

const setTest = (stepId, changes) => {
  tests.value = {
    ...tests.value,
    [stepId]: { ...(tests.value[stepId] ?? {}), ...changes },
  };
};

const runTest = async values => {
  const stepId = openStepId.value;
  const connection = openRow.value?.step?.connection;
  const id = connectionIdByName.value[connection];
  if (id === undefined) {
    setTest(stepId, {
      running: false,
      result: null,
      errorMessage: t('WASLO.TASKS.EDITOR.FLOW.LIVE.NO_ID'),
      fieldErrors: {},
    });
    return;
  }
  setTest(stepId, { running: true, errorMessage: '', fieldErrors: {} });
  try {
    const result = await wasloApi.tryConnection(accountId.value, id, values);
    // `result.ok === false` is a SUCCESSFUL test: the call was made and her own system
    // refused. That is an answer, and it is shown as one.
    setTest(stepId, {
      running: false,
      result,
      errorMessage: '',
      fieldErrors: {},
    });
  } catch (error) {
    setTest(stepId, {
      running: false,
      result: null,
      fieldErrors: error?.fields ?? {},
      errorMessage: error?.fields
        ? ''
        : t('WASLO.TASKS.EDITOR.FLOW.LIVE.UNAVAILABLE'),
    });
  }
};

const loadParams = async name => {
  const id = connectionIdByName.value[name];
  if (id === undefined) return;
  try {
    const detail = await wasloApi.getConnection(accountId.value, id);
    connectionParams.value = {
      ...connectionParams.value,
      [name]: detail.params,
    };
    connectionContactDetails.value = {
      ...connectionContactDetails.value,
      [name]: detail.contactDetails ?? [],
    };
  } catch {
    // `null` means "we could not ask", which the input list renders as its own sentence
    // rather than as "this connection takes nothing".
    connectionParams.value = { ...connectionParams.value, [name]: null };
  }
};

const usedConnections = computed(() => [
  ...new Set(
    rows.value
      .map(row => row.step)
      .filter(body => body?.type === 'choose_live' || body?.type === 'call')
      .map(body => body.connection)
      .filter(Boolean)
  ),
]);

watch(
  [usedConnections, connectionIdByName],
  ([names]) => {
    names.forEach(name => {
      if (!(name in connectionParams.value)) loadParams(name);
    });
  },
  { immediate: true }
);

watch(
  [choicesHaveIds, usedConnections],
  ([haveIds, names]) => {
    if (haveIds || names.length === 0) return;
    if (fullConnections.data.value === null) fullConnections.reload();
  },
  { immediate: true }
);

/**
 * What a real "Try it" produced for the step that is open, as the customer would read it.
 *
 * Only ever from a real answer: a live step with no sample has nothing honest to draw, and
 * inventing three rows would be a preview of a task that does not exist.
 */
const liveFor = computed(() => {
  const result = tests.value[openStepId.value]?.result;
  if (!result?.ok) return { labels: [], total: 0, more: false };
  const open = openRow.value?.step ?? {};
  return liveSample(
    result.data,
    {
      listPath: open.listPath ?? '',
      labelField: open.labelField ?? '',
      valueField: open.valueField ?? '',
      format: open.format ?? 'text',
    },
    locale.value
  );
});

// ── unsaved work ───────────────────────────────────────────────────────────────────
const warnIfUnsaved = event => {
  if (!isDirty.value && !isNew.value) return;
  event.preventDefault();
  event.returnValue = '';
};

onMounted(() => window.addEventListener('beforeunload', warnIfUnsaved));
onBeforeUnmount(() =>
  window.removeEventListener('beforeunload', warnIfUnsaved)
);

onBeforeRouteLeave(to => {
  if (bypassGuard.value) {
    bypassGuard.value = false;
    return true;
  }
  // Moving between this composer's own screens is not leaving it.
  if (to.name === 'waslo_task_new' || to.name === 'waslo_task_edit')
    return true;
  if (!isDirty.value && !isNew.value) return true;
  // The work is kept in this browser either way; the question is only whether she meant to.
  // eslint-disable-next-line no-alert
  return window.confirm(t('WASLO.TASKS.EDITOR.FLOW.LEAVE_WARNING'));
});

const listUrl = computed(() =>
  accountId.value === null
    ? null
    : frontendURL(`accounts/${accountId.value}/waslo/tasks`)
);

/**
 * F3.6: the task was removed. Its kept draft goes with it, and the page leaves for the list
 * without asking about unsaved work: there is nothing left to save it to. Declared under
 * `listUrl`, which it reads.
 */
const onTaskRemoved = async () => {
  clearLocalDraft(accountId.value, taskId.value);
  kept.value = null;
  useAlert(t('WASLO.TASKS.REMOVE.REMOVED'));
  bypassGuard.value = true;
  if (listUrl.value) await router.replace(listUrl.value);
};

const headerTitle = computed(() => {
  if (isNew.value) return t('WASLO.TASKS.EDITOR.NEW_TITLE');
  return draft.value.title.trim() || t('WASLO.TASKS.TITLE');
});

const reload = () => {
  if (!isNew.value) loadTask();
  connections.reload();
};

const goTo = target => {
  if (accountId.value === null) return;
  router.push(linkFor(target));
};

/** The Check screen's "Turn this task on", then the menu option a described task offered (P-8.2). */
const turnOn = async () => {
  await save(true);
  if (savedWire.value?.enabled) await addDescribedOption();
};
</script>

<template>
  <WasloPageLayout
    :header-title="headerTitle"
    :back-url="listUrl || ''"
    :back-label="t('WASLO.TASKS.TITLE')"
    :is-fetching="isFetching"
    :error-message="errorMessage"
    @retry="reload"
  >
    <template v-if="!isFetching && !errorMessage" #controls>
      <div class="flex flex-col gap-2 pb-4">
        <ComposeRail
          :steps="shownSteps"
          :current="step"
          :reachable="reachable"
          :link-for="linkFor"
        />
        <!--
          Only when a step is not open. A step screen has its own "Step 3 of 5" — the step
          within the TASK — and two different position lines on one screen, counting two
          different things, is the kind of thing that makes a product feel untrustworthy
          for a reason nobody can name.
        -->
        <p
          v-if="!openRow"
          class="mb-0 text-sm text-n-slate-11"
          data-waslo-compose-position
        >
          {{ position }}
        </p>
        <!--
          WHETHER THIS THING IS ANSWERING CUSTOMERS, said at the top of every screen.
          Read on arrival, before anything is typed — which is where the reader who found
          this was standing when the footer told him the opposite.
        -->
        <p
          class="mb-0 text-sm leading-6 max-w-[72ch]"
          :class="isLive ? 'text-n-blue-11' : 'text-n-slate-11'"
          data-waslo-compose-live
          :data-live="liveState"
        >
          {{ liveLine }}
        </p>
      </div>
    </template>

    <template #body>
      <div class="flex flex-col gap-6">
        <!-- Work kept in this browser, offered rather than applied. -->
        <div
          v-if="kept"
          class="flex flex-col gap-3 p-4 outline outline-1 rounded-xl outline-n-weak bg-n-solid-1"
          data-waslo-compose-kept
        >
          <p class="mb-0 text-sm text-n-slate-12">
            {{ t(key('KEPT'), { minutes: keptAgeMinutes }) }}
          </p>
          <div class="flex flex-wrap items-center gap-3">
            <Button
              :label="t(key('KEPT_TAKE'))"
              variant="solid"
              color="blue"
              size="sm"
              data-waslo-compose-kept-take
              @click="takeKeptDraft"
            />
            <Button
              :label="t(key('KEPT_DISCARD'))"
              variant="faded"
              color="slate"
              size="sm"
              data-waslo-compose-kept-discard
              @click="discardKeptDraft"
            />
          </div>
        </div>

        <JobScreen
          v-if="step === 'job'"
          :title="draft.title"
          :trigger="draft.trigger"
          :offer-starters="rows.length === 0"
          :screens="shownSteps"
          :problems="problemsForStep('job', draft, composeCtx)"
          :opening="draft.opening"
          :start="draft.start"
          :channels="draft.channels"
          :channel-options="channelOptions"
          :menus-starting="menusStarting"
          :languages="languages"
          :account-channels="accountChannelList.data.value ?? []"
          @update:title="value => (draft = { ...draft, title: value })"
          @update:trigger="value => (draft = { ...draft, trigger: value })"
          @update:opening="value => (draft = { ...draft, opening: value })"
          @update:start="value => (draft = { ...draft, start: value })"
          @update:channels="value => (draft = { ...draft, channels: value })"
          @use-starter="useStarter"
          @use-described="useDescribed"
        />

        <template v-else-if="step === 'steps'">
          <WiringScreen
            v-if="showsWiring"
            :row="openRow"
            :params="connectionParams[openRow.step?.connection] ?? null"
            :contact-details="
              connectionContactDetails[openRow.step?.connection] ?? []
            "
            :available-keys="availableKeys"
            :some-keys="someOnlyKeys"
            :taken-keys="takenKeysFor(openIndex)"
            :test="tests[openStepId] ?? {}"
            :back-link="stepLinkFor(openStepId)"
            @update:row="row => onReplace(openIndex, row)"
            @try="runTest"
          />
          <StepScreen
            v-else-if="openRow"
            :row="openRow"
            :index="openIndex"
            :rows="rows"
            :problems="byStep[openStepId] ?? []"
            :taken-keys="takenKeysFor(openIndex)"
            :contact-fields="CONTACT_FIELDS"
            :connections="graphConnections"
            :live="liveFor"
            :link-for="stepLinkFor"
            :list-link="stepsLink"
            :languages="languages"
            :move-problem="moveProblemFor"
            @update:row="row => onReplace(openIndex, row)"
            @move="delta => onMove({ index: openIndex, delta })"
            @remove="onRemove(openIndex)"
            @wiring="router.push(wiringLink)"
          />
          <template v-else>
            <p
              v-if="unreadable"
              class="mb-0 text-sm text-n-amber-11"
              data-waslo-compose-unreadable
            >
              {{ t(key('UNREADABLE')) }}
            </p>
            <ul
              v-if="unreadable && unreadableReasons.length"
              class="flex flex-col gap-1 mb-0"
              data-waslo-compose-unreadable-reasons
            >
              <li
                v-for="reason in unreadableReasons"
                :key="reason.code"
                class="text-sm text-n-slate-11"
              >
                <bdi>{{ reason.message }}</bdi>
              </li>
            </ul>
            <SpineList
              :rows="rows"
              :problems="stepProblems"
              :link-for="stepLinkFor"
              :move-problem="moveProblemFor"
              @add="onAdd"
              @move="onMove"
            />
          </template>
        </template>

        <WhatIfScreen
          v-else-if="step === 'whatif'"
          :rows="rows"
          :definition="definition"
          :sample-paths="samplePathsByConnection"
          @update:row="({ index, row }) => onReplace(index, row)"
        />

        <!--
          The sweep is fed the spine itself: `cardWords` reads `{rows}` natively, so the
          screen sees the draft as it stands rather than a copy that could fall behind it.
          Its translator is the batch route: one confirmed call, one charge (P-7.1).
        -->
        <ArabicScreen
          v-else-if="step === 'arabic'"
          :source="draft.spine ?? { rows }"
          lang="ar"
          :translator="translateCardWords"
          :warning-link="checkLink"
          @update="onCardWords"
        />

        <CheckScreen
          v-else-if="step === 'check'"
          :definition="definition"
          :title="draft.title"
          :trigger="draft.trigger"
          :jumps="jumps"
          :live="isLive"
          :pending="isNew || isDirty"
          :is-saving="isSaving"
          :problems="problems"
          :change-notes="changeNotes"
          :warnings="warnings"
          :start="startOf(definition)"
          :no-menu="noMenu"
          :rehearsal="rehearsal"
          @try="openRehearsal"
          @fix="onFix"
          @turn-on="turnOn"
          @turn-off="save(false)"
          @save="save(null)"
          @select="id => router.push(stepLinkFor(id))"
        >
          <template #words>
            <DescribedNotes
              v-if="described"
              v-model:menu-choice="menuChoice"
              :described="described"
              :menus="menus"
            />
            <BilingualBox
              v-if="formCapable"
              :en="draft.cta?.en ?? ''"
              :ar="draft.cta?.ar ?? ''"
              :label="t(key('CHECK.CTA'))"
              :hint="t(key('CHECK.CTA_HINT'))"
              :max-length="MAX_CTA"
              kind="task_cta"
              :languages="languages"
              data-waslo-compose-cta
              @update:en="value => setCta({ en: value })"
              @update:ar="value => setCta({ ar: value })"
              @update:pair="pair => setCta({ en: pair.en, ar: pair.ar })"
            />
            <MissingLanguageNotice :rows="missingWords" @apply="applyWords" />
            <!-- P-4.10: the task's form, and what this draft took off it. -->
            <div
              v-if="formLink"
              class="flex flex-col gap-2 p-4 rounded-xl bg-n-alpha-1"
              data-waslo-compose-form
            >
              <p class="mb-0 text-sm text-n-slate-12">
                {{ t('WASLO.FORMS.ARRANGE.CHECK_LINE') }}
              </p>
              <router-link
                :to="formLink"
                class="text-sm underline text-n-blue-11"
                data-waslo-compose-form-link
              >
                {{ t('WASLO.FORMS.ARRANGE.OPEN') }}
              </router-link>
            </div>
            <p
              v-for="note in formRemoved"
              :key="note.stepId"
              class="mb-0 text-sm text-n-amber-11"
              data-waslo-compose-form-removed
            >
              <bdi>{{
                t('WASLO.FORMS.ARRANGE.WARN_GONE', { question: note.question })
              }}</bdi>
            </p>
          </template>
        </CheckScreen>
        <!-- P-2.11: the rehearsal drawer, on the draft exactly as the Check screen has it. -->
        <RehearsalDrawer
          v-if="step === 'check'"
          ref="rehearsalDrawer"
          :task-id="isNew ? null : taskId"
          :definition="definition"
          :title="draft.title"
          :trigger="draft.trigger"
          @tried="loadRehearsal"
        />

        <!-- P-6.11: this task's saves, on the screen where she decides what goes live. -->
        <RestoreHistory
          v-if="!isNew && step === 'check'"
          ref="historyRef"
          :account-id="accountId"
          kind="task"
          :item-id="taskId"
          @restored="onTaskRestored"
        />

        <!-- F3.6: remove this task, confirmed in place. It can be put back from the list. -->
        <TaskRemove
          v-if="!isNew && step === 'check'"
          :account-id="accountId"
          :task-id="taskId"
          @removed="onTaskRemoved"
        />

        <!-- Back and Next. Never a disabled primary: one sentence naming what is missing. -->
        <div
          v-if="!showsWiring && !openRow && step !== 'check'"
          class="flex flex-wrap items-center justify-between gap-3"
          data-waslo-compose-nav
        >
          <Button
            v-if="previousStep"
            :label="t(key('PREVIOUS'))"
            variant="faded"
            color="slate"
            data-waslo-compose-previous
            @click="goTo(previousStep)"
          />
          <span v-else />
          <Button
            v-if="nextStep && canAdvance"
            :label="t(key('NEXT'))"
            variant="solid"
            color="blue"
            data-waslo-compose-next
            @click="goTo(nextStep)"
          />
          <p
            v-else-if="nextStep"
            class="mb-0 text-sm text-n-amber-11"
            data-waslo-compose-needs
          >
            {{ needsLine }}
          </p>
        </div>
      </div>
    </template>

    <!-- Docked, outside the scrollport, on screen at every scroll position. -->
    <template #footer>
      <WasloSaveBar
        :state="saveState"
        :status="saveStatus"
        :detail="saveDetail"
        :note="liveNote"
        :note-tone="isLive ? 'on' : 'off'"
        :save-label="saveLabel"
        :disabled="isSaving || problems.length > 0"
        @save="save(null)"
      />
    </template>
  </WasloPageLayout>
</template>
